import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { Loader2 } from "lucide-react"
import * as React from "react"

import { cn } from "@repo/ui/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "glass text-white hover:glass-darker",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-white/30 bg-transparent text-white hover:glass-darker",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "text-white hover:bg-white/10",
        link: "text-white underline-offset-4 hover:underline",
        solid: "btn-solid",
        glass:
          "bg-opacity-10 backdrop-blur-md border border-white/10 text-white shadow-lg hover:bg-white/10",
        "glass-lighter":
          "bg-white/10 backdrop-blur-md border border-white/10 text-white shadow-lg hover:bg-white/5",
        "glass-darker":
          "bg-opacity-40 backdrop-blur-md border border-white/10 text-white shadow-lg hover:bg-white/30",
        loading:
          "bg-opacity-20 backdrop-blur-md border border-white/20 text-white shadow-lg disabled:opacity-80 disabled:pointer-events-none",
      },
      size: {
        default: "h-10 px-4 py-2",
        xs: "h-8 px-2 py-1",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
        "icon-sm": "h-8 w-8",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  isLoading?: boolean
  loadingText?: string
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      isLoading,
      loadingText,
      children,
      disabled,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button"

    // Use the loading variant when isLoading is true
    const actualVariant = isLoading ? "loading" : variant

    return (
      <Comp
        className={cn(
          buttonVariants({ variant: actualVariant, size, className }),
        )}
        disabled={disabled || isLoading}
        {...props}
        ref={ref}
      >
        <>
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {isLoading && loadingText ? loadingText : children}
        </>
      </Comp>
    )
  },
)
Button.displayName = "Button"

export { Button, buttonVariants }
